// Loan Document Service
// Handles mortgage document upload and AI classification for loan applications
// Queue-based async processing: Upload → Queue → Worker (GPU) → Classify → Split (if needed)

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  LoanDocumentType,
  DocumentClassification,
  ApplicationDocument
} from '@/types/loan-documents';
import { reserveLoanStorageObject } from '@/services/loanStorageAuthorizationService';
import type { RealtimeSubscription } from '@supabase/supabase-js';

const log = createComponentLogger('LoanDocumentService', 'loan-documents');

// ============================================================================
// Configuration
// ============================================================================

const STORAGE_BUCKET = 'loan-documents';
// No file size limit - removed per user request

// Supported file types
const SUPPORTED_PDF_TYPES = ['application/pdf'];
const SUPPORTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif'
];
const ALL_SUPPORTED_TYPES = [...SUPPORTED_PDF_TYPES, ...SUPPORTED_IMAGE_TYPES];

/**
 * Check if file type is a supported image
 */
export function isImageFile(file: File): boolean {
  return SUPPORTED_IMAGE_TYPES.includes(file.type);
}

/**
 * Check if file type is a PDF
 */
export function isPdfFile(file: File): boolean {
  return SUPPORTED_PDF_TYPES.includes(file.type);
}

/**
 * Get human-readable list of supported formats
 */
export function getSupportedFormatsString(): string {
  return 'PDF, PNG, JPEG, WebP, GIF, HEIC';
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get current user's organization ID
 */
async function getCurrentUserOrganizationId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  // Try cache first
  const { data: context } = await supabase
    .from('auth_security_context')
    .select('organization_id')
    .eq('user_id', user.id)
    .maybeSingle();

  if (context?.organization_id) {
return context.organization_id;
}

  // Fallback: query users table directly (source of truth)
  const { data: userData, error: userError } = await supabase
    .from('users')
    .select('organization_id')
    .eq('id', user.id)
    .single();

  if (userError) {
    throw new Error(`Failed to fetch user organization: ${userError.message}`);
  }

  if (!userData?.organization_id) {
    throw new Error('User organization not found');
  }

  return userData.organization_id;
}

// ============================================================================
// Public API
// ============================================================================

export interface UploadLoanDocumentParams {
  file: File;
  encompassLoanId: string;
  title?: string;
  /** Optional document type to categorize the upload (e.g., 'investor_lock') */
  documentType?: LoanDocumentType;
  onProgress?: (percentage: number) => void;
}

/**
 * Queue-based document upload (non-blocking)
 * Returns immediately with queue ID for status tracking
 */
export async function uploadLoanDocument(
  params: UploadLoanDocumentParams
): Promise<ApplicationDocument> {
  const { file, encompassLoanId, title, documentType, onProgress } = params;

  log.info('Starting loan document upload (queue-based)', {
    fileName: file.name,
    fileSize: file.size,
    encompassLoanId
  });

  // Validate file type (PDF and images supported)
  if (!ALL_SUPPORTED_TYPES.includes(file.type)) {
    throw new Error(`Unsupported file type. Supported formats: ${getSupportedFormatsString()}`);
  }

  const fileIsImage = isImageFile(file);

  try {
    // Get organization ID
    const organizationId = await getCurrentUserOrganizationId();
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('User not authenticated');
    }

    // Step 1: Upload to Supabase Storage (30%)
    onProgress?.(30);
    // Use encompassLoanId prefix for consistent path structure with split children
    const fileName = `${encompassLoanId}/${Date.now()}-${file.name}`;
    await reserveLoanStorageObject(fileName, encompassLoanId);
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(fileName, file);

    if (uploadError) {
      throw new Error(`Storage upload failed: ${uploadError.message}`);
    }

    log.info('File uploaded to storage', { fileName: uploadData.path });

    // Investor-lock path: RPC owns insert + queue + audit in one transaction
    if (documentType === 'investor_lock') {
      onProgress?.(70);
      const { data: rpcData, error: rpcError } = await supabase.rpc('upload_investor_lock', {
        p_encompass_loan_id: encompassLoanId,
        p_storage_path: fileName,
        p_title: title || file.name,
        p_original_filename: file.name,
        p_file_size_bytes: file.size,
        p_mime_type: file.type
      });

      if (rpcError || !rpcData || rpcData.length === 0) {
        await supabase.storage.from(STORAGE_BUCKET).remove([fileName]);
        const message = rpcError?.message || 'Investor lock upload RPC returned no rows';
        log.error('Investor lock RPC failed', { error: message, encompassLoanId }, rpcError ?? undefined);
        throw new Error(`Investor lock upload failed: ${message}`);
      }

      const { document_id: newDocId, queue_id: newQueueId } = rpcData[0];
      onProgress?.(100);

      log.info('Investor lock uploaded via RPC', { documentId: newDocId, queueId: newQueueId });

      return {
        id: newDocId,
        encompassLoanId,
        title: title || file.name,
        fileName: file.name,
        originalFilename: file.name,
        fileSize: file.size,
        uploadedBy: user.email || user.id,
        uploadedAt: new Date().toISOString(),
        classification: { type: 'investor_lock', confidence: 1.0 },
        status: 'queued',
        queueId: newQueueId,
        queueStatus: 'pending',
        queueProgress: 0,
        queueError: null,
        isCurrentVersion: true,
        storageUrl: fileName
      };
    }

    // Step 2: Create document record (50%)
    onProgress?.(50);
    const documentData = {
      organization_id: organizationId,
      encompass_loan_id: encompassLoanId,
      title: title || file.name,
      original_filename: file.name, // Preserve original filename before AI renaming
      content: '', // Will be filled by worker service (PDFs only)
      document_type: (documentType || (fileIsImage ? 'supporting_document' : 'unknown')) as LoanDocumentType,
      category: null,
      keywords: [],
      source_file: fileName,
      file_size: file.size,
      uploaded_by: user.email || user.id,
      status: fileIsImage ? 'uploaded' : 'queued', // Images: uploaded directly, PDFs: queued
      version: 1,
      metadata: {
        originalFileName: file.name,
        uploadedAt: new Date().toISOString(),
        mimeType: file.type,
        isImage: fileIsImage
      }
    };

    const { data: insertedDoc, error: insertError } = await supabase
      .from('loan_documents')
      .insert(documentData)
      .select()
      .single();

    if (insertError) {
      // Clean up uploaded file on database insert failure
      await supabase.storage.from(STORAGE_BUCKET).remove([fileName]);
      log.error('Database insert failed', {
        error: insertError.message,
        code: insertError.code,
        details: insertError.details,
        hint: insertError.hint,
        encompassLoanId,
        documentType,
        fileSize: file.size
      });
      throw new Error(`Database insert failed: ${insertError.message}${insertError.hint ? ` (${insertError.hint})` : ''}`);
    }

    log.info('Document record created', { documentId: insertedDoc.id, isImage: fileIsImage });

    // For images, skip the queue and return directly
    if (fileIsImage) {
      onProgress?.(100);
      log.info('Image uploaded successfully (no processing needed)', { documentId: insertedDoc.id });

      return {
        id: insertedDoc.id,
        encompassLoanId,
        title: insertedDoc.title,
        fileName: file.name,
        fileSize: file.size,
        uploadedBy: insertedDoc.uploaded_by,
        uploadedAt: insertedDoc.created_at,
        classification: { type: 'supporting_document', confidence: 1.0 },
        status: 'uploaded' as const,
        storageUrl: fileName
      };
    }

    // Step 3: Create queue entry for PDFs (70%)
    onProgress?.(70);
    const { data: queueEntry, error: queueError } = await supabase
      .from('document_processing_queue')
      .insert({
        organization_id: organizationId,
        user_id: user.id,  // Track which user uploaded the document
        encompass_loan_id: encompassLoanId,
        document_id: insertedDoc.id,
        storage_path: fileName,
        file_size_bytes: file.size,
        status: 'pending',
        progress: 0
      })
      .select()
      .single();

    if (queueError) {
      // Update document status to error if queue creation fails
      await supabase
        .from('loan_documents')
        .update({
          status: 'error',
          metadata: {
            ...insertedDoc.metadata,
            error: `Queue creation failed: ${queueError.message}`
          }
        })
        .eq('id', insertedDoc.id);

      throw new Error(`Failed to queue document: ${queueError.message}`);
    }

    log.info('Document queued for processing', {
      documentId: insertedDoc.id,
      queueId: queueEntry.id
    });

    onProgress?.(100);

    // Return ApplicationDocument with queued status
    return {
      id: insertedDoc.id,
      encompassLoanId,
      title: insertedDoc.title,
      fileName: file.name,
      fileSize: file.size,
      uploadedBy: insertedDoc.uploaded_by,
      uploadedAt: insertedDoc.created_at,
      classification: undefined, // Will be set after processing
      status: 'processing',
      queueId: queueEntry.id, // Include queue ID for status tracking
      storageUrl: fileName
    };
  } catch (error) {
    log.error('Loan document upload failed', { fileName: file.name }, error as Error);
    throw error;
  }
}

/**
 * Get queue status for a document
 */
export async function getQueueStatus(queueId: string): Promise<{
  status: string;
  progress: number;
  error?: string;
  classification?: DocumentClassification;
}> {
  const { data, error } = await supabase
    .from('document_processing_queue')
    .select('status, progress, error_message, classification_result')
    .eq('id', queueId)
    .single();

  if (error || !data) {
    throw new Error('Queue entry not found');
  }

  return {
    status: data.status,
    progress: data.progress,
    error: data.error_message || undefined,
    classification: data.classification_result as DocumentClassification | undefined
  };
}

/**
 * Subscribe to real-time queue updates
 */
export function subscribeToQueueUpdates(
  queueId: string,
  onUpdate: (status: {
    status: string;
    progress: number;
    error?: string;
    classification?: DocumentClassification;
  }) => void
): RealtimeSubscription {
  log.info('Subscribing to queue updates', { queueId });

  const channel = supabase
    .channel(`queue:${queueId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'document_processing_queue',
        filter: `id=eq.${queueId}`
      },
      (payload) => {
        const { new: data } = payload;
        log.info('Queue update received via realtime', {
          queueId,
          status: data.status,
          progress: data.progress
        });
        onUpdate({
          status: data.status,
          progress: data.progress,
          error: data.error_message || undefined,
          classification: data.classification_result as DocumentClassification | undefined
        });
      }
    )
    .subscribe((status) => {
      // Log subscription state changes for debugging
      log.info('Queue subscription state changed', { queueId, state: status });
    });

  return channel;
}

/**
 * Poll queue status for documents in a loan
 */
export async function pollQueueStatusForLoan(encompassLoanId: string): Promise<Map<string, {
  documentId: string;
  status: string;
  progress: number;
  error?: string;
}>> {
  const { data, error } = await supabase
    .from('document_processing_queue')
    .select('document_id, status, progress, error_message')
    .eq('encompass_loan_id', encompassLoanId)
    .in('status', ['pending', 'extracting', 'extracted', 'classifying', 'classified', 'splitting']);

  if (error) {
    log.error('Failed to poll queue status', { encompassLoanId }, error);
    return new Map();
  }

  const statusMap = new Map();
  for (const item of data || []) {
    statusMap.set(item.document_id, {
      documentId: item.document_id,
      status: item.status,
      progress: item.progress,
      error: item.error_message
    });
  }

  return statusMap;
}

/**
 * Check if a parent document has children (used to detect split completion when queue status is stuck)
 */
export async function checkForChildDocuments(parentDocId: string): Promise<boolean> {
  const { data } = await supabase
    .from('loan_document_relationships')
    .select('id')
    .eq('parent_document_id', parentDocId)
    .limit(1);
  return (data?.length ?? 0) > 0;
}

/**
 * Get all documents for a loan application (including both parents and children for hierarchy display)
 * Also fetches processing timing stats for parent documents
 */
export async function getApplicationDocuments(
  encompassLoanId: string
): Promise<ApplicationDocument[]> {
  log.info('Fetching loan documents with processing stats', { encompassLoanId });

  // Fetch documents
  const { data, error } = await supabase
    .from('loan_documents')
    .select('*')
    .eq('encompass_loan_id', encompassLoanId)
    // No filter - include both parents and children for hierarchy display
    .order('is_parent_document', { ascending: false }) // Parents first
    .order('created_at', { ascending: false });

  if (error) {
    log.error('Failed to fetch loan documents', { encompassLoanId }, error);
    throw new Error(`Failed to fetch documents: ${error.message}`);
  }

  // Get all document IDs that could have processing stats
  // This includes parent documents AND single-doc PDFs that didn't get split
  const allDocIds = data.map(d => d.id);

  // Fetch processing stats for all documents (not just parents)
  // Also fetch queue ID and status for realtime subscription support after page reload
  const processingStatsMap = new Map<string, {
    queueId?: string;
    queueStatus?: string;
    extractionTimeMs?: number;
    classificationTimeMs?: number;
    splitTimeMs?: number;
    startedAt?: string;
    completedAt?: string;
    extractedCharacters?: number;
    totalPages?: number;
  }>();

  if (allDocIds.length > 0) {
    const { data: queueData, error: queueError } = await supabase
      .from('document_processing_queue')
      .select('id, document_id, status, extraction_time_ms, classification_time_ms, split_time_ms, started_at, completed_at, extracted_char_count, total_pages')
      .in('document_id', allDocIds);

    if (queueError) {
      log.warn('Failed to fetch processing stats', { error: queueError.message });
    } else if (queueData) {
      for (const q of queueData) {
        processingStatsMap.set(q.document_id, {
          queueId: q.id,
          queueStatus: q.status,
          extractionTimeMs: q.extraction_time_ms,
          classificationTimeMs: q.classification_time_ms,
          splitTimeMs: q.split_time_ms,
          startedAt: q.started_at,
          completedAt: q.completed_at,
          extractedCharacters: q.extracted_char_count,
          totalPages: q.total_pages
        });
      }
    }
  }

  // Count children for each parent document
  // Note: parent_document_id is stored in metadata, not as a column
  const childCountMap = new Map<string, number>();
  data.forEach(doc => {
    const parentId = doc.metadata?.parent_document_id;
    if (parentId) {
      childCountMap.set(parentId, (childCountMap.get(parentId) || 0) + 1);
    }
  });

  log.info('Loan documents fetched', {
    encompassLoanId,
    count: data.length,
    parents: data.filter(d => d.is_parent_document).length,
    children: data.filter(d => !d.is_parent_document && d.metadata?.parent_document_id).length,
    withStats: processingStatsMap.size
  });

  // Map database records to ApplicationDocument type
  return data.map(doc => {
    const stats = processingStatsMap.get(doc.id);
    const totalTimeMs = stats
      ? (stats.extractionTimeMs || 0) + (stats.classificationTimeMs || 0) + (stats.splitTimeMs || 0)
      : undefined;

    return {
      id: doc.id,
      encompassLoanId: doc.encompass_loan_id,
      title: doc.smart_filename || doc.title, // Use smart filename if available
      fileName: doc.smart_filename || doc.metadata?.originalFileName || doc.source_file,
      originalFilename: doc.original_filename || doc.metadata?.originalFileName || undefined,
      fileSize: doc.file_size,
      uploadedBy: doc.uploaded_by,
      uploadedAt: doc.created_at,
      classification: doc.metadata?.classification || { type: doc.document_type, confidence: 1.0 },
      status: doc.status as ApplicationDocument['status'],
      error: doc.metadata?.error,
      storageUrl: doc.source_file,
      // Add hierarchy fields
      isParentDocument: doc.is_parent_document || false,
      parentDocumentId: doc.metadata?.parent_document_id || undefined,
      pageRange: doc.metadata?.page_range || undefined,
      // Queue ID for realtime subscription support after page reload
      queueId: stats?.queueId,
      // Add processing stats for parent documents
      processingStats: stats ? {
        ...stats,
        totalTimeMs,
        childCount: childCountMap.get(doc.id) || 0
      } : undefined,
      // Include raw metadata for additional fields like split_completed
      metadata: doc.metadata || {}
    };
  });
}

/**
 * Get investor lock documents for a loan
 * Returns only documents with document_type = 'investor_lock'
 */
export async function getInvestorLockDocuments(
  encompassLoanId: string
): Promise<ApplicationDocument[]> {
  log.info('Fetching investor lock documents', { encompassLoanId });

  const { data, error } = await supabase
    .from('loan_documents')
    .select('*')
    .eq('encompass_loan_id', encompassLoanId)
    .eq('document_type', 'investor_lock')
    .order('is_current_version', { ascending: false })
    .order('version', { ascending: false });

  if (error) {
    log.error('Failed to fetch investor lock documents', { encompassLoanId }, error);
    throw new Error(`Failed to fetch investor lock documents: ${error.message}`);
  }

  // Get live queue state for each document (status, progress, error)
  const docIds = data.map(d => d.id);
  const queueMap = new Map<
    string,
    { queueId: string; status: string; progress: number; errorMessage: string | null }
  >();

  if (docIds.length > 0) {
    const { data: queueData } = await supabase
      .from('document_processing_queue')
      .select('id, document_id, status, progress, error_message')
      .in('document_id', docIds);

    if (queueData) {
      for (const q of queueData) {
        queueMap.set(q.document_id, {
          queueId: q.id,
          status: q.status,
          progress: q.progress,
          errorMessage: q.error_message ?? null
        });
      }
    }
  }

  log.info('Investor lock documents fetched', { encompassLoanId, count: data.length });

  return data.map(doc => {
    const q = queueMap.get(doc.id);
    return {
      id: doc.id,
      encompassLoanId: doc.encompass_loan_id,
      title: doc.smart_filename || doc.title,
      fileName: doc.smart_filename || doc.metadata?.originalFileName || doc.source_file,
      originalFilename: doc.original_filename || doc.metadata?.originalFileName || undefined,
      fileSize: doc.file_size,
      uploadedBy: doc.uploaded_by,
      uploadedAt: doc.created_at,
      classification: { type: 'investor_lock' as LoanDocumentType, confidence: 1.0 },
      status: doc.status as ApplicationDocument['status'],
      error: doc.metadata?.error,
      storageUrl: doc.source_file,
      queueId: q?.queueId,
      queueStatus: (q?.status ?? null) as ApplicationDocument['queueStatus'],
      queueProgress: q?.progress ?? null,
      queueError: q?.errorMessage ?? null,
      version: doc.version,
      isCurrentVersion: Boolean(doc.is_current_version),
      metadata: doc.metadata || {}
    };
  });
}

/**
 * Promote an investor_lock document to current via RPC.
 * Pass expectedPriorCurrentId for concurrency-safe undo: if the actual prior
 * current differs, the RPC raises 'concurrent_change_detected' and we return
 * { success: false, reason: 'concurrent_change_detected' }.
 */
export async function setInvestorLockCurrent(
  documentId: string,
  options: { expectedPriorCurrentId?: string | null } = {}
): Promise<{ success: true } | { success: false; reason: 'concurrent_change_detected' | 'error'; message?: string }> {
  log.info('Setting investor lock current', { documentId, expectedPriorCurrentId: options.expectedPriorCurrentId ?? null });

  const { error } = await supabase.rpc('set_investor_lock_current', {
    p_document_id: documentId,
    p_expected_prior_current_id: options.expectedPriorCurrentId ?? null
  });

  if (error) {
    if (error.message?.includes('concurrent_change_detected')) {
      log.warn('Set current skipped: concurrent change detected', { documentId });
      return { success: false, reason: 'concurrent_change_detected' };
    }
    log.error('Failed to set investor lock current', { documentId }, error);
    return { success: false, reason: 'error', message: error.message };
  }

  return { success: true };
}

/**
 * Delete an investor_lock document via RPC, then best-effort remove the
 * storage object. If it was current, the RPC auto-promotes the highest
 * remaining version in the same transaction.
 */
export async function deleteInvestorLockDocument(
  documentId: string
): Promise<{ promotedId: string | null }> {
  log.info('Deleting investor lock document', { documentId });

  // Fetch source_file first so we can remove the storage object after DB delete
  const { data: doc } = await supabase
    .from('loan_documents')
    .select('source_file')
    .eq('id', documentId)
    .maybeSingle();

  const { data: promotedId, error } = await supabase.rpc('delete_investor_lock', {
    p_document_id: documentId
  });

  if (error) {
    log.error('Failed to delete investor lock document', { documentId }, error);
    throw new Error(`Failed to delete investor lock document: ${error.message}`);
  }

  // Best-effort storage cleanup (orphaned objects are tolerated in v1)
  if (doc?.source_file) {
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([doc.source_file]);

    if (storageError) {
      log.warn('Failed to remove storage object after delete', { file: doc.source_file }, storageError);
    }
  }

  return { promotedId: (promotedId as string | null) ?? null };
}

/**
 * Update document classification manually (override AI)
 */
export async function updateDocumentClassification(
  documentId: string,
  type: LoanDocumentType,
  confidence: number = 1.0
): Promise<void> {
  log.info('Updating document classification', { documentId, type });

  const { data: doc, error: fetchError } = await supabase
    .from('loan_documents')
    .select('metadata')
    .eq('id', documentId)
    .single();

  if (fetchError) {
    throw new Error(`Failed to fetch document: ${fetchError.message}`);
  }

  const metadata = doc.metadata || {};
  const classification: DocumentClassification = {
    type,
    confidence,
    reasoning: 'Manually set by user'
  };

  metadata.classification = classification;
  metadata.manuallyClassified = true;
  metadata.classifiedAt = new Date().toISOString();

  const { error: updateError } = await supabase
    .from('loan_documents')
    .update({
      document_type: type,
      status: 'classified',
      metadata
    })
    .eq('id', documentId);

  if (updateError) {
    throw new Error(`Failed to update classification: ${updateError.message}`);
  }

  log.info('Document classification updated', { documentId, type });
}

/**
 * Update document title (rename)
 * Updates both title and smart_filename since display uses: smart_filename || title
 */
export async function updateDocumentTitle(
  documentId: string,
  newTitle: string
): Promise<void> {
  const trimmedTitle = newTitle.trim();

  if (!trimmedTitle) {
    throw new Error('Document title cannot be empty');
  }

  log.info('Updating document title', { documentId, newTitle: trimmedTitle });

  // Update both title and smart_filename since display prioritizes smart_filename
  const { data, error } = await supabase
    .from('loan_documents')
    .update({
      title: trimmedTitle,
      smart_filename: trimmedTitle
    })
    .eq('id', documentId)
    .select();

  if (error) {
    throw new Error(`Failed to update document title: ${error.message}`);
  }

  if (!data || data.length === 0) {
    throw new Error('No document was updated - check if document exists and you have permission');
  }

  log.info('Document title updated', { documentId, newTitle: trimmedTitle });
}

/**
 * Delete a loan document (cascades to children if parent)
 */
export async function deleteDocument(documentId: string): Promise<void> {
  log.info('Deleting document', { documentId });

  // 1. First, find children by querying documents with this parent ID in metadata
  // NOTE: We query loan_documents directly instead of loan_document_relationships
  // because CASCADE on the relationship table can delete rows before we query them
  const { data: children } = await supabase
    .from('loan_documents')
    .select('id')
    .contains('metadata', { parent_document_id: documentId });

  if (children?.length) {
    log.info('Deleting child documents first', { count: children.length });
    for (const child of children) {
      await deleteDocument(child.id);
    }
  }

  // 2. Fetch document to get storage file path (use maybeSingle to avoid 406)
  const { data: doc, error: fetchError } = await supabase
    .from('loan_documents')
    .select('source_file')
    .eq('id', documentId)
    .maybeSingle();

  if (fetchError) {
    log.warn('Failed to fetch document for deletion', { documentId }, fetchError);
    // Continue anyway - document may already be deleted
  }

  // 3. Delete from database (CASCADE will delete relationship rows if any)
  const { error: deleteError } = await supabase
    .from('loan_documents')
    .delete()
    .eq('id', documentId);

  if (deleteError) {
    throw new Error(`Failed to delete document: ${deleteError.message}`);
  }

  // 4. Delete from storage
  if (doc?.source_file) {
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([doc.source_file]);

    if (storageError) {
      log.warn('Failed to delete file from storage', { file: doc.source_file }, storageError);
      // Don't throw - database record already deleted
    }
  }

  log.info('Document deleted', { documentId });
}

/**
 * Download document URL
 */
export async function getDocumentDownloadUrl(storageUrl: string): Promise<string> {
  const { data } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(storageUrl, 300);

  if (!data) {
    throw new Error('Failed to generate download URL');
  }

  return data.signedUrl;
}

// ============================================================================
// Queue Management for Extraction
// ============================================================================

/**
 * Queue a document for extraction processing
 * Used for processing pre-existing documents that were uploaded before the queue system
 */
export async function queueDocumentForProcessing(
  documentId: string
): Promise<{ queueId: string }> {
  log.info('Queueing document for processing', { documentId });

  // Get current user and org
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
throw new Error('User not authenticated');
}

  const { data: securityContext } = await supabase
    .from('auth_security_context')
    .select('organization_id')
    .eq('user_id', user.id)
    .single();

  if (!securityContext?.organization_id) {
throw new Error('User has no organization');
}

  // Get document details
  const { data: doc, error: docError } = await supabase
    .from('loan_documents')
    .select('id, encompass_loan_id, source_file, file_size')
    .eq('id', documentId)
    .single();

  if (docError || !doc) {
    throw new Error('Document not found');
  }

  // Check if already in queue
  const { data: existing } = await supabase
    .from('document_processing_queue')
    .select('id, status')
    .eq('document_id', documentId)
    .single();

  if (existing) {
    // If failed, allow reprocessing
    if (existing.status === 'failed') {
      const { error: updateError } = await supabase
        .from('document_processing_queue')
        .update({
          status: 'pending',
          progress: 0,
          error_message: null,
          error_details: null,
          retry_count: 0
        })
        .eq('id', existing.id);

      if (updateError) {
throw updateError;
}
      log.info('Document re-queued for processing', { documentId, queueId: existing.id });
      return { queueId: existing.id };
    }
    // Otherwise, return existing queue entry
    log.info('Document already in queue', { documentId, queueId: existing.id, status: existing.status });
    return { queueId: existing.id };
  }

  // Create new queue entry
  const { data: queueEntry, error: queueError } = await supabase
    .from('document_processing_queue')
    .insert({
      organization_id: securityContext.organization_id,
      user_id: user.id,
      encompass_loan_id: doc.encompass_loan_id,
      document_id: doc.id,
      storage_path: doc.source_file,
      file_size_bytes: doc.file_size,
      status: 'pending',
      progress: 0
    })
    .select('id')
    .single();

  if (queueError) {
throw queueError;
}

  log.info('Document queued for processing', { documentId, queueId: queueEntry.id });
  return { queueId: queueEntry.id };
}

/**
 * Queue multiple documents for extraction processing (bulk operation)
 */
export async function queueDocumentsForProcessing(
  documentIds: string[]
): Promise<{ queued: number; skipped: number; failed: number }> {
  log.info('Bulk queueing documents for processing', { count: documentIds.length });

  let queued = 0;
  let skipped = 0;
  let failed = 0;

  for (const documentId of documentIds) {
    try {
      await queueDocumentForProcessing(documentId);
      queued++;
    } catch (err) {
      const error = err as Error;
      if (error.message.includes('already in queue')) {
        skipped++;
      } else {
        log.error('Failed to queue document', { documentId }, error);
        failed++;
      }
    }
  }

  log.info('Bulk queue complete', { queued, skipped, failed });
  return { queued, skipped, failed };
}

// ============================================================================
// Encompass Attachment Extraction
// ============================================================================

/**
 * Queue an Encompass attachment for extraction processing
 * Only works for attachments with download_status = 'downloaded'
 */
export async function queueEncompassAttachmentForProcessing(
  attachmentId: string
): Promise<{ queueId: string }> {
  log.info('Queueing Encompass attachment for processing', { attachmentId });

  // Get current user and org
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
throw new Error('User not authenticated');
}

  const { data: securityContext } = await supabase
    .from('auth_security_context')
    .select('organization_id')
    .eq('user_id', user.id)
    .single();

  if (!securityContext?.organization_id) {
throw new Error('User has no organization');
}

  // Get attachment details directly (no need to join through encompass_documents)
  const { data: attachment, error: attachError } = await supabase
    .from('encompass_attachments')
    .select(`
      id,
      storage_path,
      storage_bucket,
      file_size,
      download_status,
      encompass_loan_id
    `)
    .eq('id', attachmentId)
    .single();

  if (attachError || !attachment) {
    throw new Error('Attachment not found');
  }

  if (attachment.download_status !== 'downloaded') {
    throw new Error(`Attachment not ready: status is ${attachment.download_status}`);
  }

  if (!attachment.storage_path) {
    throw new Error('Attachment has no storage path');
  }

  if (!attachment.encompass_loan_id) {
    throw new Error('Attachment has no encompass_loan_id');
  }

  // Check if already in queue
  const { data: existing } = await supabase
    .from('document_processing_queue')
    .select('id, status')
    .eq('encompass_attachment_id', attachmentId)
    .maybeSingle();

  if (existing) {
    // If failed, allow reprocessing
    if (existing.status === 'failed') {
      const { error: updateError } = await supabase
        .from('document_processing_queue')
        .update({
          status: 'pending',
          progress: 0,
          error_message: null,
          error_details: null,
          retry_count: 0
        })
        .eq('id', existing.id);

      if (updateError) {
throw updateError;
}
      log.info('Encompass attachment re-queued for processing', { attachmentId, queueId: existing.id });
      return { queueId: existing.id };
    }
    // Otherwise, return existing queue entry
    log.info('Encompass attachment already in queue', { attachmentId, queueId: existing.id, status: existing.status });
    return { queueId: existing.id };
  }

  // Create new queue entry for Encompass attachment
  const { data: queueEntry, error: queueError } = await supabase
    .from('document_processing_queue')
    .insert({
      organization_id: securityContext.organization_id,
      user_id: user.id,
      encompass_loan_id: attachment.encompass_loan_id,
      encompass_attachment_id: attachmentId,
      source_type: 'encompass',
      storage_path: attachment.storage_path,
      file_size_bytes: attachment.file_size,
      status: 'pending',
      progress: 0
    })
    .select('id')
    .single();

  if (queueError) {
throw queueError;
}

  log.info('Encompass attachment queued for processing', { attachmentId, queueId: queueEntry.id });
  return { queueId: queueEntry.id };
}

/**
 * Queue multiple Encompass attachments for extraction processing (bulk operation)
 */
export async function queueEncompassAttachmentsForProcessing(
  attachmentIds: string[]
): Promise<{ queued: number; skipped: number; failed: number }> {
  log.info('Bulk queueing Encompass attachments for processing', { count: attachmentIds.length });

  let queued = 0;
  let skipped = 0;
  let failed = 0;

  for (const attachmentId of attachmentIds) {
    try {
      await queueEncompassAttachmentForProcessing(attachmentId);
      queued++;
    } catch (err) {
      const error = err as Error;
      if (error.message.includes('already in queue')) {
        skipped++;
      } else if (error.message.includes('not ready') || error.message.includes('no storage path')) {
        skipped++;
        log.warn('Skipped attachment - not downloaded', { attachmentId });
      } else {
        log.error('Failed to queue Encompass attachment', { attachmentId }, error);
        failed++;
      }
    }
  }

  log.info('Bulk Encompass queue complete', { queued, skipped, failed });
  return { queued, skipped, failed };
}

/**
 * Queue an AI document for extraction and embedding processing
 * Used for AI knowledge base documents that need chunking + Cohere embeddings
 */
export async function queueAIDocumentForProcessing(
  documentId: string
): Promise<{ queueId: string }> {
  log.info('Queueing AI document for processing', { documentId });

  // Get current user and org
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
throw new Error('User not authenticated');
}

  const { data: securityContext } = await supabase
    .from('auth_security_context')
    .select('organization_id')
    .eq('user_id', user.id)
    .single();

  if (!securityContext?.organization_id) {
throw new Error('User has no organization');
}

  // Get AI document details
  const { data: aiDocument, error: docError } = await supabase
    .from('ai_documents')
    .select('id, title, source_file, file_size, status')
    .eq('id', documentId)
    .single();

  if (docError || !aiDocument) {
    throw new Error('AI document not found');
  }

  // Check if already in queue
  const { data: existing } = await supabase
    .from('document_processing_queue')
    .select('id, status')
    .eq('ai_document_id', documentId)
    .maybeSingle();

  if (existing) {
    // If failed, allow reprocessing
    if (existing.status === 'failed') {
      const { error: updateError } = await supabase
        .from('document_processing_queue')
        .update({
          status: 'pending',
          progress: 0,
          error_message: null,
          error_details: null,
          retry_count: 0
        })
        .eq('id', existing.id);

      if (updateError) {
throw updateError;
}
      log.info('AI document re-queued for processing', { documentId, queueId: existing.id });
      return { queueId: existing.id };
    }
    // Otherwise, return existing queue entry
    log.info('AI document already in queue', { documentId, queueId: existing.id, status: existing.status });
    return { queueId: existing.id };
  }

  // Build storage path from source_file if available
  // AI documents may store file in ai-documents bucket
  const storagePath = aiDocument.source_file || `ai-documents/${documentId}`;

  // Create new queue entry for AI document
  const { data: queueEntry, error: queueError } = await supabase
    .from('document_processing_queue')
    .insert({
      organization_id: securityContext.organization_id,
      user_id: user.id,
      ai_document_id: documentId,
      source_type: 'ai',
      storage_path: storagePath,
      file_size_bytes: aiDocument.file_size,
      status: 'pending',
      progress: 0
    })
    .select('id')
    .single();

  if (queueError) {
throw queueError;
}

  // Update AI document status to processing
  await supabase
    .from('ai_documents')
    .update({ status: 'processing' })
    .eq('id', documentId);

  log.info('AI document queued for processing', { documentId, queueId: queueEntry.id });
  return { queueId: queueEntry.id };
}
